import {
  Component,
  computed,
  inject,
  input,
  resource,
  signal,
} from '@angular/core';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SplitButtonModule } from 'primeng/splitbutton';
import { InputTextModule } from 'primeng/inputtext';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Message } from 'primeng/message';
import { Rolle } from '../rolle.model';
import { RolleDetailService } from './rolle-detail.service';
import { PanelModule } from 'primeng/panel';
import { TextareaModule } from 'primeng/textarea';
import { RolleRettighederComponent } from '../rolle-rettigheder/rolle-rettigheder.component';
import { RolleDatasource } from '@features/roller/rolle.datasource';
import { Checkbox } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { SimpleApi } from '@core/api/simple.api';
import { Rettighed } from '../rettighed.model';

@Component({
  selector: 'app-rolle-detail',
  imports: [
    ToolbarModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    SplitButtonModule,
    InputTextModule,
    ReactiveFormsModule,
    Message,
    PanelModule,
    TextareaModule,
    RolleRettighederComponent,
    RouterLink,
    Checkbox,
    Select,
  ],
  templateUrl: './rolle-detail.component.html',
  styleUrl: './rolle-detail.component.css',
})
export class RolleDetailComponent {
  id = input.required<number>();

  simpleApi = inject(SimpleApi);
  datasource = inject(RolleDatasource);
  rolleDetailService = inject(RolleDetailService);
  router = inject(Router);

  validationMessage = signal<string | undefined>(undefined);

  data = resource({
    params: () => this.id(),
    loader: async ({ params, abortSignal }) => {
      const rolle = await this.rolleDetailService.datasource.getById(
        params,
        abortSignal
      );
      this.form.patchValue(rolle);
      return rolle;
    },
  });

  rettighedData = resource({
    loader: async ({ abortSignal }) => {
      return await this.simpleApi.getList<Rettighed>(
        '/api/rettighederenum',
        {},
        abortSignal
      );
    },
  });

  rolle = computed(() => {
    const rolle = this.data.value();
    return rolle || ({} as Rolle);
  });

  form = new FormGroup({
    navn: new FormControl('', {
      validators: [Validators.required],
    }),
    beskrivelse: new FormControl(''),
    adminRettighed: new FormControl(''),
    tilladLokalAdmin: new FormControl(false),
    tilladEkstern: new FormControl(false),
  });

  get navnIsInvalid() {
    return (
      this.form.controls.navn.touched &&
      this.form.controls.navn.dirty &&
      this.form.controls.navn.invalid
    );
  }

  rettighedOptions = computed(() =>
    this.rettighedData.value()?.filter(r => r.name.startsWith('RolleAdm'))
  );

  async onClickSave() {
    this.validationMessage.set(undefined);
    if (this.form.invalid) {
      return;
    }
    const newData = {
      ...this.rolle(),
      ...this.form.value,
    } as Rolle;

    try {
      const rolle = await this.rolleDetailService.saveRolle(this.id(), newData);
      await this.rolleDetailService.saveRolleRettigheder(rolle.id);
      await this.router.navigate(['/brugeradm/roller']);
    } catch (error) {
      console.error(error);
      let message;
      if (error instanceof Error) message = error.message;
      else message = String(error);
      this.validationMessage.set(message);
    }
  }
}
